import type {
  IntegrationOverviewData,
  PartnerEcosystemData,
  PartnerProgramData,
  PendingActionData,
  PsRevenueData,
} from "../../../features/admin-home-dashboard/types";
import {
  DEFAULT_REGION_ACCENT_COLOR,
  DEFAULT_TIER_COLOR,
  getTechnologyCustomIcon,
  PENDING_ACTION_CONFIG,
  PARTNER_TIER_COLORS,
  PARTNER_PROGRAM_TIER_ORDER,
  REGION_ACCENT_COLORS,
  slugify,
} from "../../../features/admin-home-dashboard/constants/dashboardVisualConfig";
import type {
  AdminHomeDashboardStatsData,
  IntegrationItemAPI,
  IntegrationTechnologyAPI,
  RevenueStreamAPI,
} from "./Types";

const UNAVAILABLE_VALUE = "Not Available";

/** Remove trailing year from labels like "Aug 2026" → "Aug". */
const stripTrailingYear = (label: string): string =>
  label.replace(/\s+\d{4}\s*$/, "").trim();

const formatFullCurrency = (amount: number): string => {
  if (amount === 0) {
    return UNAVAILABLE_VALUE;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const mapIntegrationBreakdown = (
  integrations: IntegrationItemAPI[] = []
) =>
  integrations.map((item) => ({
    label: item.integration_name.toUpperCase(),
    value: item.count,
  }));

const mapRevenueStream = (
  stream: RevenueStreamAPI | undefined,
  year: number,
  totalKey: "total_revenue" | "total_arr",
  waivedKey: "waived_revenue" | "waived_arr",
  monthlyKey: "monthly_revenue" | "monthly_arr"
): Pick<
  PsRevenueData,
  "totalRevenue" | "totalArr" | "revenueBreakdown" | "arrBreakdown" | "waivedRevenue" | "waivedArr"
> => {
  const total = stream?.[totalKey] ?? 0;
  const waived = stream?.[waivedKey] ?? 0;
  const monthly = stream?.[monthlyKey] ?? [];

  const breakdown = monthly.map((item) => ({
    label:
      totalKey === "total_arr" ? stripTrailingYear(item.month) : item.month,
    amount: formatFullCurrency(item.revenue),
  }));

  if (totalKey === "total_revenue") {
    return {
      totalRevenue: formatFullCurrency(total),
      totalArr: "",
      revenueBreakdown: breakdown,
      arrBreakdown: [],
      waivedRevenue: formatFullCurrency(waived),
      waivedArr: "",
    };
  }

  return {
    totalRevenue: "",
    totalArr: formatFullCurrency(total),
    revenueBreakdown: [],
    arrBreakdown: breakdown,
    waivedRevenue: "",
    waivedArr: formatFullCurrency(waived),
  };
};

export const mapPendingActions = (
  pendingActions: AdminHomeDashboardStatsData["pending_actions"]
): PendingActionData[] =>
  PENDING_ACTION_CONFIG.map((config) => ({
    id: config.id,
    title: config.title,
    subtitle: config.subtitle,
    count: config.isStaticDisabled
      ? 0
      : (pendingActions[config.apiKey] ?? 0),
    icon: config.icon,
    customIcon: config.customIcon,
    isStaticDisabled: config.isStaticDisabled,
  }));

export const mapPartnerEcosystem = (
  partnerEcosystem: AdminHomeDashboardStatsData["partner_ecosystem"]
): PartnerEcosystemData => {
  const rows =
    partnerEcosystem.segment ??
    partnerEcosystem.regions?.map((region) => ({
      segment: region.region,
      total_partners: region.total_partners,
      active: region.active,
      registered: region.registered,
      lapsed: region.lapsed,
      inactive: region.inactive,
    })) ??
    [];

  return {
    totalPartners: partnerEcosystem.total_partners ?? 0,
    activePartners: partnerEcosystem.active_partners ?? 0,
    regions: rows.map((row) => ({
      region: row.segment,
      active: row.active ?? 0,
      registered: row.registered ?? 0,
      lapsed: row.lapsed ?? 0,
      inactive: row.inactive ?? 0,
      total: row.total_partners ?? 0,
      accentColor:
        REGION_ACCENT_COLORS[row.segment] ?? DEFAULT_REGION_ACCENT_COLOR,
    })),
  };
};

export const mapPartnerProgram = (
  partnerProgram: AdminHomeDashboardStatsData["partner_program"],
  evaluationYear: number
): PartnerProgramData => {
  const tiers = (partnerProgram.tiers ?? []).map((tier) => ({
    id: slugify(tier.tier),
    label: tier.tier,
    count: tier.count ?? 0,
    color: PARTNER_TIER_COLORS[tier.tier] ?? DEFAULT_TIER_COLOR,
  }));

  const tierOrder = new Map<string, number>(
    PARTNER_PROGRAM_TIER_ORDER.map((label, index) => [label, index])
  );

  tiers.sort((a, b) => {
    const aOrder = tierOrder.get(a.label) ?? PARTNER_PROGRAM_TIER_ORDER.length;
    const bOrder = tierOrder.get(b.label) ?? PARTNER_PROGRAM_TIER_ORDER.length;
    return aOrder - bOrder;
  });

  return {
    totalPartners: Number(partnerProgram.total_partners ?? 0) || 0,
    evaluationYear,
    tiers,
  };
};

export const mapPsRevenue = (
  psRevenue: AdminHomeDashboardStatsData["ps_revenue"],
  evaluationYear: number
): PsRevenueData => {
  const revenueMapped = mapRevenueStream(
    psRevenue.revenue,
    evaluationYear,
    "total_revenue",
    "waived_revenue",
    "monthly_revenue"
  );
  const arrMapped = mapRevenueStream(
    psRevenue.arr,
    evaluationYear,
    "total_arr",
    "waived_arr",
    "monthly_arr"
  );

  return {
    year: evaluationYear,
    totalRevenue: revenueMapped.totalRevenue,
    totalArr: arrMapped.totalArr,
    revenueGrowthPercent:
      psRevenue.revenue?.growth_percentage != null
        ? Number(psRevenue.revenue.growth_percentage)
        : undefined,
    arrGrowthPercent:
      psRevenue.arr?.growth_percentage != null
        ? Number(psRevenue.arr.growth_percentage)
        : undefined,
    revenueBreakdown: revenueMapped.revenueBreakdown,
    arrBreakdown: arrMapped.arrBreakdown,
    waivedRevenue: revenueMapped.waivedRevenue,
    waivedArr: arrMapped.waivedArr,
  };
};

const mapIntegrationRow = (technology: IntegrationTechnologyAPI) => ({
  id: slugify(technology.technology),
  technology: technology.technology,
  customIcon: getTechnologyCustomIcon(technology.technology),
  inProgress: technology.in_progress?.total_integrations ?? 0,
  inProgressBreakdown: mapIntegrationBreakdown(
    technology.in_progress?.integrations
  ),
  completed: technology.completed?.total_integrations ?? 0,
  completedBreakdown: mapIntegrationBreakdown(
    technology.completed?.integrations
  ),
  avgDurationDays:
    technology.average_duration?.average_duration_days ?? 0,
  minDays: technology.average_duration?.minimum_duration_days ?? 0,
  maxDays: technology.average_duration?.maximum_duration_days ?? 0,
});

export const mapIntegrationOverview = (
  integrationOverview: AdminHomeDashboardStatsData["integration_overview"]
): IntegrationOverviewData => ({
  rows: (integrationOverview.data ?? []).map(mapIntegrationRow),
  status: {
    overallCompleted: integrationOverview.summary?.overall_completed ?? 0,
    liveValidation: integrationOverview.summary?.live_validation ?? 0,
    inProgress: integrationOverview.summary?.overall_in_progress ?? 0,
  },
});

export const EMPTY_PARTNER_ECOSYSTEM: PartnerEcosystemData = {
  totalPartners: 0,
  activePartners: 0,
  regions: [],
};

export const EMPTY_PARTNER_PROGRAM = (
  evaluationYear: number
): PartnerProgramData => ({
  totalPartners: 0,
  evaluationYear,
  tiers: [],
});

export const EMPTY_PS_REVENUE = (year: number): PsRevenueData => ({
  year,
  totalRevenue: UNAVAILABLE_VALUE,
  totalArr: UNAVAILABLE_VALUE,
  revenueBreakdown: [],
  arrBreakdown: [],
  waivedRevenue: UNAVAILABLE_VALUE,
  waivedArr: UNAVAILABLE_VALUE,
});

export const EMPTY_INTEGRATION_OVERVIEW: IntegrationOverviewData = {
  rows: [],
  status: {
    overallCompleted: 0,
    liveValidation: 0,
    inProgress: 0,
  },
};

export const EMPTY_PENDING_ACTIONS: PendingActionData[] =
  PENDING_ACTION_CONFIG.map((config) => ({
    id: config.id,
    title: config.title,
    subtitle: config.subtitle,
    count: 0,
    icon: config.icon,
    customIcon: config.customIcon,
  }));

export const parseEvaluationYear = (
  date?: string
): number => {
  if (date && !Number.isNaN(Number(date))) {
    return Number(date);
  }

  return new Date().getFullYear();
};
